import { run } from '@ag1/ui';
import { PageTranslation } from '@portal/schema/studio/studio';
import { JobItem } from '@portal/shared/studio/JobList/types';
import { getActiveBannerForHeader } from '@portal/shared/utils/bannerVisibility';
import type { HeaderContextData } from '@portal/state/context/HeaderContext';
import { MediaProvider } from '@portal/state/context/MediaContext';
import { PageSection, PageSectionType } from '@portal/types/studio/page';
import { PromoBanner } from '@portal/types/studio/studio';
import { Fragment } from 'react';
import { getComponent } from './componentsMap';
import PreviewErrorBoundary from './PreviewErrorBoundary';
import { fetchUsedSanityProducts, isSectionType, pageSectionPropsBuilder } from './utils';

const DynamicShopSectionTypes = [
  'shopV3Banners',
  'shopV3Bundles',
  'shopV3Products'
] as const satisfies PageSectionType[];

const DeprecatedSectionTypes = [
  'headerFull',
  'buildBundle',
  'buildBundle3Months',
  'buildBundleV2',
  'offerBaB',
  'subAndSaveBaB',
  'addToCartSection',
  'hero'
] as const satisfies PageSectionType[];

/**
 * Creates a component for a section based on its type.
 */
export const renderSection = (
  item: PageSection,
  {
    preview,
    headerData,
    productsInventory,
    usedProductsMap,
    activeBanner,
    activePromoBanner,
    pageTranslationsLinks,
    jobs,
    visibleOnPageLoad = false
  }: {
    preview: boolean;
    headerData: HeaderContextData;
    productsInventory: Awaited<ReturnType<typeof fetchUsedSanityProducts>>['productsInventory'];
    usedProductsMap: Awaited<ReturnType<typeof fetchUsedSanityProducts>>['usedProductsMap'];
    activeBanner: ReturnType<typeof getActiveBannerForHeader>;
    activePromoBanner?: PromoBanner;
    pageTranslationsLinks?: PageTranslation[];
    jobs?: JobItem[];
    visibleOnPageLoad?: boolean;
  }
) => {
  const { _key: key } = item;

  let child = run(() => {
    // Deprecated sections shouldn't render.
    if (isSectionType(item, DeprecatedSectionTypes)) {
      return null;
    }

    const Component = getComponent(item._type);
    if (!Component) {
      return null;
    }

    // jobList component requires jobs being passed in.
    if (item._type === 'jobList' && !jobs) {
      return null;
    }

    const props = pageSectionPropsBuilder(item)
      .addForTypes(['headerFullV2', 'headerLanderV3'], { activeBanner, activePromoBanner })
      .addForTypes(['headerFullV2'], {
        header: headerData.header,
        logoSVG: headerData.logoSVG,
        translations: pageTranslationsLinks
      })
      // If we're at this point with jobList, the jobs are defined since we
      // checked for existence earlier.
      .addForTypes(['jobList'], { jobs: jobs! })
      .addForTypes(['buildBundleV3', 'addToCartSectionShop'], {
        productsInventory
      })
      .addForTypes(['addToCartSectionShop', 'simplifiedBaB', 'offerV3', 'buildBundleV3'], {
        usedProductsMap
      })
      .getProps();

    return <Component {...props} visibleOnPageLoad={visibleOnPageLoad} />;
  });

  if (child) {
    // These dynamic shop sections need a media provider context.
    if (isSectionType(item, DynamicShopSectionTypes)) {
      child = <MediaProvider>{child}</MediaProvider>;
    }

    child = (
      <PreviewErrorBoundary preview={preview} section={item._type}>
        {child}
      </PreviewErrorBoundary>
    );
  }

  return <Fragment key={key}>{child}</Fragment>;
};
